import { PLATFORM_NAMES } from '@lib/utils/platforms';
import { ColumnConfig } from './ColumnTypes';

export const COLUMN_DEFINITIONS: Record<string, ColumnConfig> = {
  filename: {
    field: 'filename',
    headerName: 'Media name',
    flex: 1,
    // Leave room for the name while fitting the default columns on a desktop.
    minWidth: 440,
    sortable: true,
    filterable: true,
    hideable: false,
    disableReorder: true,
    type: 'string',
  },
  menu: {
    field: 'menu',
    disableReorder: true,
    // In reach without scrolling sideways, on a table wider than the window.
    pinned: 'right',
    headerName: '',
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    hideable: false,
    resizable: false,
  },
  duration: {
    field: 'duration',
    headerName: 'Duration',
    flex: 0.4,
    minWidth: 120,
    sortable: true,
    filterable: true,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
  },
  size: {
    field: 'size',
    headerName: 'Size',
    flex: 0.4,
    minWidth: 120,
    sortable: true,
    filterable: true,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
  },
  frameCount: {
    field: 'frameCount',
    headerName: 'Frame Count',
    flex: 0.4,
    minWidth: 120,
    sortable: true,
    filterable: true,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
  },
  width: {
    field: 'width',
    headerName: 'Width',
    flex: 0.3,
    minWidth: 80,
    sortable: true,
    filterable: true,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
  },
  channel: {
    field: 'channel',
    headerName: 'Source',
    flex: 0.3,
    minWidth: 100,
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    align: 'center',
    headerAlign: 'center',
    valueOptions: PLATFORM_NAMES,
  },
  height: {
    field: 'height',
    headerName: 'Height',
    flex: 0.3,
    minWidth: 80,
    sortable: true,
    filterable: true,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
  },
  status: {
    field: 'status',
    headerName: 'Status',
    flex: 0.3,
    // Fits the longest pill label, "Processing (100%)" (101px in 11px Inter semibold), inside
    // the cell's 12px side padding without touching the pill's rounded ends.
    minWidth: 150,
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    align: 'center',
    headerAlign: 'center',
    valueOptions: ['uploading', 'queued', 'processing', 'completed', 'errored', 'cancelled'],
  },
  publishedAt: {
    field: 'publishedAt',
    headerName: 'Published',
    flex: 0.4,
    minWidth: 140,
    sortable: true,
    filterable: true,
    type: 'dateTime',
    align: 'center',
    headerAlign: 'center',
  },
  createdAt: {
    field: 'createdAt',
    headerName: 'Added',
    flex: 0.4,
    minWidth: 140,
    sortable: true,
    filterable: true,
    type: 'dateTime',
    align: 'center',
    headerAlign: 'center',
  },
  youtubeId: {
    field: 'youtubeId',
    headerName: 'YouTube ID',
    flex: 0.4,
    minWidth: 140,
    sortable: true,
    filterable: true,
    type: 'string',
    align: 'center',
    headerAlign: 'center',
  },
  isYouTubeShort: {
    field: 'isYouTubeShort',
    headerName: 'YT Short?',
    flex: 0.2,
    minWidth: 100,
    sortable: true,
    filterable: true,
    type: 'singleSelect',
    align: 'center',
    headerAlign: 'center',
    valueOptions: [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ],
  },
};

export const MEDIA_TRAILING_COLUMNS = ['menu'];

export const DEFAULT_COLUMN_ORDER = [
  'filename',
  'duration',
  'size',
  'publishedAt',
  'createdAt',
  'status',
  'channel',
  'youtubeId',
  'isYouTubeShort',
  'frameCount',
  'width',
  'height',
  'menu',
];

/**
 * Default visibility for every *togglable* column — which is to say every column except the two
 * marked `hideable: false` above (`filename`, `menu`) and MUI's own `__check__`.
 *
 * This key set has to stay complete: it is what `useGridColumnPreferences` persists and what feeds
 * the Manage-columns panel. A hideable column missing from here can be hidden for the session and
 * is then silently forgotten — which is what happened to `duration`, `status` and `channel` until
 * they were added.
 */
export const DEFAULT_VISIBILITY = {
  frameCount: false,
  width: false,
  height: false,
  publishedAt: false,
  createdAt: true,
  size: false,
  youtubeId: false,
  isYouTubeShort: false,
  duration: true,
  status: true,
  channel: true,
};
